<script setup lang="ts">
import type { PropType } from 'vue'
import type { NavItem } from '../UiNavigation.types'
import type { INavButton } from '../UiNav/UiNav.types'
import { useRouteLink } from '@/composables/useRouteLink'

const { routeLink } = useRouteLink()
const location = useRoute()
const { t } = useI18n()
const openElementId = ref('')
const openSubElementId = ref('')
const navButtons: INavButton[] | undefined = inject('navButtons')

const props = defineProps({
  navigation: {
    type: Object as PropType<NavItem[]>,
    required: true,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['toggle-menu'])

function toggleIntercomVisibility(hide: boolean) {
  if (import.meta.client) {
    const intercom = document.querySelector('.intercom-lightweight-app, #intercom-container')
    if (intercom) {
      (intercom as HTMLElement).style.display = hide ? 'none' : ''
    }
  }
}

watch(
  () => props.isOpen,
  (value) => {
    toggleIntercomVisibility(value)
  },
)

function toggle(name: string) {
  if (openElementId.value === name) {
    openElementId.value = ''
  } else {
    openElementId.value = name
  }
  openSubElementId.value = ''
}

function toggleSub(name: string) {
  if (openSubElementId.value === name) {
    openSubElementId.value = ''
  } else {
    openSubElementId.value = name
  }
}

function visibleItems(items?: NavItem[]) {
  return items?.filter(item => t(item.label)) ?? []
}

function showLang() {
  return !location.fullPath.includes('/o-benefit-systems/kariera')
}

function onButtonClick(callback: () => void) {
  emit('toggle-menu')

  if (callback) {
    callback()
  }
}

function toggleMenu(): void {
  emit('toggle-menu')
}

</script>

<template>
  <nav
    :class="[
      'navigation-mobile',
      { 'is-open': isOpen },
    ]"
  >
    <ul class="navigation-mobile__list">
      <li
        v-for="(itemFirst, index) in navigation"
        :key="itemFirst.link"
        class="navigation-mobile__list_item--first"
        @click="toggle(`firstIndex-${index}`)"
      >
        <span
          v-if="itemFirst.children?.length"
          :class="[
            'navigation-mobile__list_item-link',
            'navigation-mobile__list_item-dropdown',
            { 'is-active' : openElementId === `firstIndex-${index}` }
          ]"
        >
          {{ $t(itemFirst.label) }}
        </span>

        <NuxtLink
          v-else-if="$t(itemFirst.link) && !itemFirst.external"
          class="navigation-mobile__list_item-link"
          :to="routeLink($t(itemFirst.link), undefined, itemFirst.linkToLocale)"
          @click="toggleMenu()"
          >
          {{ $t(itemFirst.label) }}
        </NuxtLink>

        <a
          v-else
          class="navigation-mobile__list_item-link"
          :href="$t(itemFirst.link)"
        >
          {{ $t(itemFirst.label) }}
        </a>

        <transition name="slide">
          <ul
            v-if="itemFirst.children?.length"
            v-show="openElementId === `firstIndex-${index}`"
            :class="[
              'navigation-mobile__list',
              { 'navigation-mobile__list--submenu': itemFirst.isSubMenu },
            ]"
            @click.stop
          >
            <li
              v-for="(itemSecond, secondIndex) in visibleItems(itemFirst.children)"
              :key="itemSecond.label"
              class="navigation-mobile__list_item--second"
            >
              <span
                v-if="itemSecond.children?.length"
                :class="[
                  'navigation-mobile__list_item-link',
                  'navigation-mobile__list_item-dropdown',
                  { 'is-active': openSubElementId === `secondIndex-${index}-${secondIndex}` },
                ]"
                @click="toggleSub(`secondIndex-${index}-${secondIndex}`)"
              >
                {{ $t(itemSecond.label) }}
              </span>

              <NuxtLink
                v-else-if="$t(itemSecond.link) && !itemSecond.external"
                class="navigation-mobile__list_item-link"
                :to="routeLink($t(itemSecond.link), undefined, itemSecond.linkToLocale)"
                @click="toggleMenu()"
              >
                {{ $t(itemSecond.label) }}
              </NuxtLink>

              <a
                v-else-if="$t(itemSecond.link)"
                class="navigation-mobile__list_item-link"
                :href="$t(itemSecond.link)"
              >
                {{ $t(itemSecond.label) }}
              </a>

              <span
                v-else
                class="navigation-mobile__list_item-link"
              >
                {{ $t(itemSecond.label) }}
              </span>

              <transition name="slide">
                <ul
                  v-if="itemSecond.children?.length"
                  v-show="openSubElementId === `secondIndex-${index}-${secondIndex}`"
                  class="navigation-mobile__list navigation-mobile__list--inner"
                >
                  <li
                    v-for="itemThird in visibleItems(itemSecond.children)"
                    :key="itemThird.label"
                    class="navigation-mobile__list_item--third"
                  >
                    <NuxtLink
                      v-if="$t(itemThird.link) && !itemThird.external"
                      class="navigation-mobile__list_item-link"
                      :to="routeLink($t(itemThird.link), undefined, itemThird.linkToLocale)"
                      @click="toggleMenu()"
                    >
                      {{ $t(itemThird.label) }}
                    </NuxtLink>
              
                    <a
                      v-else-if="$t(itemThird.link)"
                      class="navigation-mobile__list_item-link"
                      :href="$t(itemThird.link)"
                    >
                      {{ $t(itemThird.label) }}
                    </a>

                    <span
                      v-else
                      class="navigation-mobile__list_item-link"
                    >
                      {{ $t(itemThird.label) }}
                    </span>
                  </li>
                </ul>
              </transition>
            </li>
          </ul>
        </transition>
      </li>
    </ul>
    <div class="navigation-mobile__buttons">
      <UiButton
        v-for="(navButton, index) in navButtons"
        :key="index"
        :size="navButton.size"
        :to="navButton.to"
        @click="onButtonClick(navButton.callback)"
      >
        {{ $t(navButton.label) }}
      </UiButton>

      <UiLanguage v-if="showLang()" />
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.navigation-mobile {
  $root: &;

  position: absolute;
  top: 7.4rem;
  left: 0;
  z-index: 9999999999;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 0;
  padding: 0 2.4rem;
  overflow-y: hidden;
  background-color: transparent;
  transition: all .5s ease-in-out;
  &.is-open {
    height: calc(100dvh - 7.4rem);
    overflow: hidden;
    background-color: $white;
  }

  &__list {
    display: flex;
    flex-direction: column;

    #{$root} > & {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }

    &_item {

      &--first {
        padding: 1.6rem 0;
        border-bottom: 1px solid $gray-300;

        #{$root}__list_item-link {
          color: $text-400;
          font-weight: 500;
          font-size: 2.4rem;
          line-height: 3.2rem;
        }
      }

      &--second {
        margin-top: 1.6rem;
        #{$root}__list_item-link {
          color: $text-300;
          font-weight: 500;
          font-size: 2rem;
          line-height: 3.2rem;
        }
      }

      &-dropdown {
        &:after {
          display: inline-block;
          width: 1.6rem;
          height: 1.6rem;
          margin-left: 0.4rem;
          vertical-align: middle;
          background-image: url('/icons/arrow_down.svg');
          background-position: center;
          background-size: cover;
          content: '';
        }

        &.is-active {
          &:after {
            transform: rotate(180deg);
          }
        }
      }
    }

    &--submenu {
      gap: 1.2rem;
      padding: 1.6rem 0 0 1.6rem;

      #{$root}__list_item--second {
        margin-top: 0;
      }
    }

    &--inner {
      gap: 1.2rem;
      padding: 1.2rem 0 0 1.6rem;
    }
  }

  &__buttons {
    display: flex;
    flex-shrink: 0;
    justify-content: space-between;
    gap: 0.8rem;
    padding: 1.6rem 0;
    background-color: $white;

    .languages {
      margin-left: auto;
    }
  }
}
</style>
